import React, { useMemo } from 'react';

import {
  HcMapMapboxLayerIds,
  LegendInterval,
  VectilesLayerSource,
  VectilesMetricGroups,
} from '@hcs/types';

import {
  SCHOOLS_LAYER_FILTER_DEF,
  SCHOOLS_LAYER_SCHOOL_KIND,
  VECTILES_METRICS_GROUP_CONFIG,
} from '../../constants';
import {
  useHcMap,
  useHcMapLayerLegendBreaks,
  useHcMapLayers,
} from '../../hooks';
import { getBlocksLayerFillDefinition, isConsumerLayerEnum } from '../../utils';

import { BlocksLayer } from './BlocksLayer';
import { SchoolLayer } from './SchoolLayer';

interface Props {
  mapId: string;
}

const dataHcName = 'hcmap-layers';

export const HcMapLayers = ({ mapId }: Props) => {
  const { mapState } = useHcMap(mapId);
  const { vectilesMetricConfig } = useHcMapLayers(mapId);
  const intervals: LegendInterval[] | undefined =
    useHcMapLayerLegendBreaks(mapId);
  const {
    vectilesMetricId,
    vectilesMetricGroup,
    showMonochrome,
    propertyType,
  } = mapState?.heatmap || {};
  const groupZoomRange = useMemo(() => {
    const groupConfig =
      vectilesMetricGroup && VECTILES_METRICS_GROUP_CONFIG[vectilesMetricGroup];
    const groupZoomRange: VectilesLayerSource['zoomRange'] = {
      max: 0,
      min: 1000,
    };
    groupConfig?.vectilesLayers.forEach(({ zoomRange }) => {
      if (zoomRange.min < groupZoomRange.min) {
        groupZoomRange.min = zoomRange.min;
      }
      if (zoomRange.max > groupZoomRange.max) {
        groupZoomRange.max = zoomRange.max;
      }
    });
    return groupZoomRange;
  }, [vectilesMetricGroup]);
  const { showMarkers } = mapState?.markers || {};

  const paint = useMemo(() => {
    if (!mapState || !vectilesMetricConfig || !groupZoomRange) {
      return null;
    }
    return {
      'fill-opacity': 0.5,
      ...getBlocksLayerFillDefinition(
        showMonochrome || false,
        propertyType,
        intervals,
        vectilesMetricConfig.vectilesMetric,
      ),
    };
  }, [
    mapState,
    vectilesMetricConfig,
    groupZoomRange,
    intervals,
    showMonochrome,
    propertyType,
  ]);
  if (
    !mapState ||
    !vectilesMetricConfig ||
    !groupZoomRange ||
    !paint ||
    !vectilesMetricId
  ) {
    return null;
  }

  const beneathLayerId = showMarkers
    ? undefined
    : HcMapMapboxLayerIds.SCHOOL_SYMBOL_LAYER;

  return (
    <div data-hc-name={dataHcName}>
      {vectilesMetricGroup && vectilesMetricId && (
        <BlocksLayer
          /* HACK(mikep): BlocksLayer can't handle changing of the keys specified in the paint object,
            so force a rerender based on hasMonochromeOverlay which changes the fill properties */
          key={`mo-${showMonochrome}`}
          mapId={mapId}
          paint={paint}
          beneathLayerId={beneathLayerId}
        />
      )}

      {vectilesMetricGroup === VectilesMetricGroups.Schools && (
        <SchoolLayer
          mapId={mapId}
          featureFilter={(feature) =>
            !!feature.properties &&
            vectilesMetricConfig.vectilesMetric &&
            isConsumerLayerEnum(vectilesMetricConfig.vectilesMetric) &&
            SCHOOLS_LAYER_FILTER_DEF[vectilesMetricConfig.vectilesMetric] ===
              feature.properties.level &&
            feature.properties.kind === SCHOOLS_LAYER_SCHOOL_KIND
          }
          changeFilterTrigger={vectilesMetricId}
        />
      )}
    </div>
  );
};
